import {
  Alert,
  message as AppMessage,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type TuttiPermission,
  type TuttiPermissionPage,
  tuttiPermissionsApi,
} from '@/services/tuttiPermissionsApi';
import { PermissionTemplatesPanel } from './Templates';

const emptyPage: TuttiPermissionPage = {
  permissions: [],
  requiredPermissions: [],
  nextKey: '',
};
function errorMessage(error: unknown): string {
  const response = (error as { response?: { status?: number } })?.response;
  if (response?.status === 409)
    return '权限已存在或已被其他管理员修改，请刷新后重试。';
  if (response?.status === 400)
    return '权限格式不正确，请检查模块、功能名称及总长度。';
  if (response?.status === 403) return '当前账号没有管理权限。';
  return '操作失败，请稍后重试。';
}

const PermissionCatalogPanel = observer(() => {
  const [message, messageContext] = AppMessage.useMessage();
  const [modal, modalContext] = Modal.useModal();
  const [page, setPage] = useState(emptyPage);
  const [cursor, setCursor] = useState('');
  const [history, setHistory] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<TuttiPermission | 'new' | null>(null);
  const [form] = Form.useForm();
  const module = Form.useWatch('module', form);
  const feature = Form.useWatch('featureKey', form);
  const action = Form.useWatch('action', form);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setError('');
    try {
      const result = await tuttiPermissionsApi.list(cursor);
      if (request === generation.current) setPage(result);
    } catch (cause) {
      if (request === generation.current) {
        setPage(emptyPage);
        setError(errorMessage(cause));
      }
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [cursor]);
  useEffect(() => {
    void load();
    return () => {
      generation.current++;
    };
  }, [load]);
  const open = (permission: TuttiPermission | 'new') => {
    form.resetFields();
    form.setFieldsValue(
      permission === 'new' ? { module: 'session', action: 'read' } : permission,
    );
    setEditing(permission);
  };
  const save = async () => {
    const value = await form.validateFields().catch(() => null);
    if (!value) return;
    if (!editing || saving) return;
    setSaving(true);
    try {
      if (editing === 'new')
        await tuttiPermissionsApi.register({
          featureKey: `${value.module}.${value.featureKey}`,
          action: value.action,
          description: value.description ?? '',
          key: `${value.module}.${value.featureKey}.${value.action}`,
        });
      else
        await tuttiPermissionsApi.update(
          editing.key,
          value.description ?? '',
          editing.description,
        );
      setEditing(null);
      message.success('权限已保存');
      await load();
    } catch (cause) {
      message.error(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };
  const registerRequired = () =>
    modal.confirm({
      title: '登记基础权限',
      content:
        '将补齐房间、会话、资源、群聊和评论的基础权限点。注册不授予任何角色访问权限；遇到定义冲突时整批停止。',
      okText: '确认登记',
      cancelText: '取消',
      onOk: async () => {
        setSaving(true);
        try {
          await tuttiPermissionsApi.registerRequired();
          message.success('基础权限已登记');
          await load();
        } catch (cause) {
          message.error(errorMessage(cause));
          throw cause;
        } finally {
          setSaving(false);
        }
      },
    });
  return (
    <div className="space-y-6">
      {messageContext}
      {modalContext}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <Typography.Title level={3}>Tutti 权限管理</Typography.Title>
          <Typography.Text type="secondary">
            按模块和功能管理全局权限点。房主、成员、游客是独立角色，在具体房间内决定授权。
          </Typography.Text>
        </div>
        <Space>
          <Button onClick={() => void load()} loading={loading}>
            刷新
          </Button>
          <Button onClick={registerRequired} disabled={saving || loading}>
            登记基础权限
          </Button>
          <Button type="primary" onClick={() => open('new')} disabled={saving}>
            注册权限
          </Button>
        </Space>
      </div>
      <Alert
        type="info"
        showIcon
        title="注册权限不会自动授权"
        description="权限格式为「模块.功能.read/write」，例如 session.comment.write。角色不包含在权限标识中；同一权限可授权给不同房间中的不同角色。"
      />
      {error && (
        <Alert
          type="error"
          showIcon
          title={error}
          action={<Button onClick={() => void load()}>重试</Button>}
        />
      )}
      <Table<TuttiPermission>
        rowKey="key"
        loading={loading}
        dataSource={page.permissions}
        pagination={false}
        scroll={{ x: 800 }}
        columns={[
          {
            title: '权限标识',
            dataIndex: 'key',
            render: (key: string) => (
              <Typography.Text code copyable>
                {key}
              </Typography.Text>
            ),
          },
          {
            title: '模块',
            dataIndex: 'featureKey',
            render: (featureKey: string) => {
              const module = featureKey.split('.')[0];
              return (
                <Tag>
                  {module === 'guest' ? '旧版角色前缀 · guest' : module}
                </Tag>
              );
            },
          },
          {
            title: '功能',
            dataIndex: 'featureKey',
            render: (value: string) =>
              value.split('.').slice(1).join('.') || value,
          },
          {
            title: '类型',
            dataIndex: 'action',
            render: (action: string) => (
              <Tag color={action === 'write' ? 'orange' : 'blue'}>
                {action === 'write' ? '写入' : '读取'}
              </Tag>
            ),
          },
          { title: '说明', dataIndex: 'description' },
          {
            title: '操作',
            render: (_, permission) =>
              !/^(guest|member|owner)\./.test(permission.key) &&
              permission.key.split('.').length >= 3 ? (
                <Button
                  type="link"
                  onClick={() => open(permission)}
                  disabled={saving}
                >
                  编辑说明
                </Button>
              ) : (
                '只读'
              ),
          },
        ]}
        locale={{
          emptyText: error
            ? '权限列表加载失败'
            : '暂无权限，可先登记基础权限或注册新的权限点',
        }}
      />
      <Space>
        <Button
          disabled={!history.length || loading}
          onClick={() => {
            setCursor(history[history.length - 1] ?? '');
            setHistory(history.slice(0, -1));
          }}
        >
          上一页
        </Button>
        <Button
          disabled={!page.nextKey || loading}
          onClick={() => {
            setHistory([...history, cursor]);
            setCursor(page.nextKey);
          }}
        >
          下一页
        </Button>
      </Space>
      <Modal
        title={editing === 'new' ? '注册权限' : '编辑权限说明'}
        open={editing !== null}
        onCancel={() => {
          if (!saving) setEditing(null);
        }}
        onOk={() => void save()}
        confirmLoading={saving}
        cancelButtonProps={{ disabled: saving }}
        maskClosable={!saving}
        closable={!saving}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          {editing === 'new' ? (
            <>
              <Form.Item
                name="module"
                label="模块"
                extra="例如 session、chat；guest、member、owner 是角色，不用作模块。"
                rules={[
                  { required: true, message: '请输入模块' },
                  {
                    pattern: /^[a-z][a-z0-9]*(?:[_-][a-z0-9]+)*$/,
                    message: '使用小写英文、数字、下划线或短横线，不能包含点',
                  },
                  { max: 32, message: '最多 32 个字符' },
                  {
                    validator: async (_, value) => {
                      if (['guest', 'member', 'owner'].includes(value))
                        throw new Error('角色与权限点独立，请填写业务模块');
                    },
                  },
                ]}
              >
                <Input placeholder="session" maxLength={32} />
              </Form.Item>
              <Form.Item
                name="featureKey"
                label="功能名称"
                dependencies={['module', 'action']}
                extra="例如 comment，不包含模块和 read / write。"
                rules={[
                  { required: true, message: '请输入功能名称' },
                  {
                    pattern: /^[a-z][a-z0-9]*(?:[._-][a-z0-9]+)*$/,
                    message: '使用小写英文、数字及点、下划线或短横线',
                  },
                  { max: 58, message: '最多 58 个字符' },
                  {
                    validator: async (_, value) => {
                      if (
                        `${form.getFieldValue('module') ?? ''}.${value ?? ''}.${form.getFieldValue('action') ?? ''}`
                          .length > 64
                      )
                        throw new Error('完整权限标识最多 64 个字符');
                    },
                  },
                ]}
              >
                <Input placeholder="comment" maxLength={58} />
              </Form.Item>
              <Form.Item
                name="action"
                label="类型"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { label: '读取（read）', value: 'read' },
                    { label: '写入（write）', value: 'write' },
                  ]}
                />
              </Form.Item>
              <Typography.Paragraph code>
                {module || '模块'}.{feature || '功能'}.{action || '动作'}
              </Typography.Paragraph>
            </>
          ) : (
            <Typography.Paragraph code>{editing?.key}</Typography.Paragraph>
          )}
          <Form.Item
            name="description"
            label="说明"
            rules={[{ max: 255, message: '最多 255 个字符' }]}
          >
            <Input.TextArea rows={3} maxLength={255} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export const TuttiPermissionsPage = () => (
  <Tabs
    items={[
      {
        key: 'catalog',
        label: '权限目录',
        children: <PermissionCatalogPanel />,
      },
      {
        key: 'templates',
        label: '默认模板',
        children: <PermissionTemplatesPanel />,
      },
    ]}
  />
);
